import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent, MutableRefObject } from "react";
import type { Chart as ChartJS } from "chart.js";

import { findMarkerAtPointer, markerDrawXOnChart } from "./plugins/markerPlugin";
import type { ChartMarkerLine } from "./TargetChart.types";

/** The marker under the pointer and where to anchor its tooltip. */
export interface HoveredMarkerState {
  /** Marker being hovered. */
  marker: ChartMarkerLine;
  /**
   * Canvas position to anchor the tooltip to: the x of the drawn line, so the
   * tooltip stays on it rather than drifting with the pointer, and the
   * pointer's own y.
   */
  x: number;
  y: number;
}

/** The marker actions menu: which marker it's for and where it opens. */
export interface MarkerMenuState {
  /** Whether the menu is visible. */
  open: boolean;
  /** Marker the menu acts on. */
  markerId: number;
  /** Viewport position of the click that opened it. */
  anchorPosition: { top: number; left: number };
}

interface UseMarkerInteractionsOptions {
  /** The rendered Chart.js instance. */
  chartRef: MutableRefObject<ChartJS<"line"> | null>;
  /** Markers currently drawn on the chart. */
  markers: ChartMarkerLine[];
  /** Whether marker lines react to the pointer at all. */
  enabled: boolean;
}

/**
 * Makes marker lines interactive: hovering one highlights it and reports where
 * to anchor its tooltip, clicking one opens its actions menu at the click
 * position.
 *
 * Hit-testing reuses the marker plugin's geometry, so a click lands exactly on
 * the drawn line. The hovered id is also exposed through a stable getter the
 * plugin reads at draw time, the same way it reads the markers.
 *
 * @param options
 * @param options.chartRef - The rendered Chart.js instance
 * @param options.markers  - Markers currently drawn on the chart
 * @param options.enabled  - Whether marker lines react to the pointer
 */
export const useMarkerInteractions = ({
  chartRef,
  markers,
  enabled,
}: UseMarkerInteractionsOptions) => {
  const [hovered, setHoveredState] = useState<HoveredMarkerState | null>(null);
  const [menu, setMenu] = useState<MarkerMenuState | null>(null);
  const hoveredMarkerIdRef = useRef<number | null>(null);

  const setHovered = useCallback(
    (next: HoveredMarkerState | null) => {
      const markerId = next?.marker.id ?? null;
      const changedMarker = hoveredMarkerIdRef.current !== markerId;
      hoveredMarkerIdRef.current = markerId;
      // The position follows the pointer, so it's updated even while the same
      // marker stays hovered.
      setHoveredState((current) =>
        !changedMarker && current?.x === next?.x && current?.y === next?.y ? current : next
      );
      // The plugin reads the hovered id at draw time — repaint to reflect it.
      if (changedMarker) chartRef.current?.draw?.();
    },
    [chartRef]
  );

  const getHoveredMarkerId = useCallback(() => hoveredMarkerIdRef.current, []);

  const markerAt = (event: MouseEvent) =>
    enabled ? findMarkerAtPointer(chartRef.current, markers, event.clientX, event.clientY) : null;

  const onMouseMove = (event: MouseEvent) => {
    const marker = markerAt(event);
    const x = marker ? markerDrawXOnChart(chartRef.current, marker) : null;
    if (!marker || x == null) {
      setHovered(null);
      return;
    }
    // Relative to the element the handler sits on — the box the canvas fills,
    // which is the space the chart's own tooltip is positioned in too.
    const top = event.currentTarget.getBoundingClientRect().top;
    setHovered({ marker, x, y: event.clientY - top });
  };

  const onMouseLeave = () => setHovered(null);

  const onClick = (event: MouseEvent) => {
    const marker = markerAt(event);
    if (!marker) return;
    setMenu({
      open: true,
      markerId: marker.id,
      anchorPosition: { top: event.clientY, left: event.clientX },
    });
  };

  // Keep the last position while closing so the exit transition doesn't jump.
  const closeMenu = useCallback(
    () => setMenu((current) => current && { ...current, open: false }),
    []
  );

  // Drop hover/menu state for markers that are gone (e.g. just deleted), or
  // when interactions are switched off.
  useEffect(() => {
    const isLive = (markerId: number | null) =>
      enabled && markerId != null && markers.some((marker) => marker.id === markerId);

    if (!isLive(hoveredMarkerIdRef.current)) setHovered(null);
    setMenu((current) =>
      current?.open && !isLive(current.markerId) ? { ...current, open: false } : current
    );
  }, [enabled, markers, setHovered]);

  return {
    hovered,
    hoveredMarkerId: hovered?.marker.id ?? null,
    getHoveredMarkerId,
    menu,
    closeMenu,
    pointerHandlers: { onMouseMove, onMouseLeave, onClick },
  };
};
